import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ExternalLink, FlaskConical, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { btnRed } from "@/components/stable/ui";

const LEVEL = {
  riesgo: ["Riesgo de positivo", "bg-red-50 border-[#DC2626] text-[#991B1B]", AlertTriangle],
  precaucion: ["Precaución: revisa estos ingredientes", "bg-amber-50 border-amber-500 text-amber-900", ShieldAlert],
  sin_coincidencias: ["Sin coincidencias con la lista", "bg-emerald-50 border-emerald-600 text-emerald-900", CheckCircle2],
};

export const DopingList = ({ info, compact }) => (
  <div className="grid sm:grid-cols-2 gap-3" data-testid="doping-list">
    {(compact ? info.natural.slice(0, 6) : info.natural).map((n) => (
      <div key={n.name} className="border border-slate-200 rounded p-4 bg-white">
        <p className="font-display font-extrabold text-lg text-[#0B1D3A]">{n.name}</p>
        <p className="text-sm text-slate-600 mt-1">{n.detail}</p>
      </div>
    ))}
  </div>
);

const Checker = ({ info }) => {
  const [formula, setFormula] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setRes(null);
    try { setRes((await api.post("/antidopaje/revisar", { formula })).data); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const [title, cls, Icon] = res ? LEVEL[res.level] : [];
  return (
    <section className="bg-[#EFF6FF] rounded p-5 sm:p-8" data-testid="doping-checker">
      <p className="font-display font-black text-3xl text-[#0B1D3A] flex items-center gap-2"><FlaskConical className="text-[#DC2626]" /> Revisa una fórmula</p>
      <p className="text-slate-700 mt-2 max-w-3xl">Pega la composición o la lista de ingredientes de un suplemento y la IA la compara con esta lista, incluidos nombres latinos y extractos.</p>
      <form onSubmit={run} className="mt-4 space-y-3">
        <textarea required minLength={3} maxLength={4000} rows={5} value={formula} onChange={(e) => setFormula(e.target.value)} placeholder="Ej.: Harpagophytum procumbens, levadura de cerveza, extracto de Salix alba, aromas…" className="w-full rounded border border-slate-300 p-3 text-sm bg-white focus:border-[#2563EB] outline-none" data-testid="doping-formula" />
        <button disabled={busy} className={btnRed} data-testid="doping-check">{busy ? <><Loader2 size={16} className="animate-spin" /> Revisando…</> : "Revisar fórmula"}</button>
      </form>
      {res && (
        <div className={`mt-6 border-l-4 rounded p-5 ${cls}`} data-testid="doping-result">
          <p className="font-display font-extrabold text-2xl flex items-center gap-2" data-testid="doping-level"><Icon size={22} /> {title}</p>
          <p className="mt-2">{res.summary}</p>
          {res.findings.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="doping-findings">
              {res.findings.map((f, i) => <li key={`${f.ingredient}-${i}`} className="bg-white/70 rounded p-3 text-sm text-[#0F172A]"><b>{f.ingredient}</b> → {f.substance}<span className="block text-slate-600 mt-0.5">{f.why}</span></li>)}
            </ul>
          )}
          {res.advice && <p className="mt-4 text-sm font-semibold">{res.advice}</p>}
          <p className="mt-4 text-xs">{info.notice} <a href={info.epsd_url} target="_blank" rel="noreferrer" className="underline font-bold">Base de datos oficial FEI (EPSD)</a>.</p>
        </div>
      )}
    </section>
  );
};

export const useDopingInfo = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/antidopaje").then((r) => setInfo(r.data)); }, []);
  return info;
};

export default function Antidoping() {
  const info = useDopingInfo();
  if (!info) return <div className="min-h-screen" />;
  return (
    <div data-testid="doping-page">
      <section className="bg-[#0B1D3A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <p className="text-[#60A5FA] font-bold text-sm tracking-[0.2em] uppercase">Competición · Actualizado {info.updated}</p>
          <h1 className="font-display font-black text-5xl sm:text-6xl mt-2">Antidopaje FEI</h1>
          <p className="text-blue-100 mt-4 max-w-2xl">Ingredientes de suplementos que con más frecuencia dan positivo en los controles de la FEI.</p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 py-12 space-y-12">
        <div className="border-2 border-amber-400 bg-amber-50 rounded p-5 text-amber-900" data-testid="doping-notice">
          <p className="font-bold flex items-center gap-2"><AlertTriangle size={18} /> Importante</p>
          <p className="mt-1 text-sm">{info.notice}</p>
          <a href={info.epsd_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-3 text-sm font-bold underline" data-testid="doping-epsd">Consultar la base de datos FEI (EPSD) <ExternalLink size={14} /></a>
        </div>
        <section>
          <h2 className="font-display font-black text-3xl text-[#0B1D3A] mb-4">Ingredientes naturales que pueden dar positivo</h2>
          <DopingList info={info} />
        </section>
        <div className="grid md:grid-cols-2 gap-5">
          <section className="border border-slate-200 rounded p-6" data-testid="doping-changes"><h2 className="font-display font-black text-2xl text-[#0B1D3A]">Cambios de la lista FEI 2026</h2><p className="text-slate-700 mt-2 text-sm">{info.changes_2026}</p></section>
          <section className="border border-slate-200 rounded p-6" data-testid="doping-cross"><h2 className="font-display font-black text-2xl text-[#0B1D3A]">Cuidado con la contaminación cruzada</h2><p className="text-slate-700 mt-2 text-sm">{info.cross}</p></section>
        </div>
        <Checker info={info} />
        <p className="text-xs text-slate-500" data-testid="doping-sources">Fuentes: {info.sources.join(" · ")}. Información orientativa; no sustituye la consulta a la FEI ni al veterinario. <Link to="/guia" className="underline">Guía de salud</Link></p>
      </div>
    </div>
  );
}
